if (!customElements.get('product-gallery-v2')) {
  // Picks object-fit per gallery image from its natural aspect ratio. The frame
  // is landscape (1.26:1). Roughly-square studio shots (sofa centred with margin)
  // fill it edge-to-edge with `cover` — the crop only eats the side/top margin, so
  // there's no cream pillarbox gap beside the next slide. Wider or taller images
  // keep `contain` so the whole product stays visible and the slide's cream
  // backdrop fills any margin. Applied as an inline style so it wins over the base
  // and desktop `object-fit: contain` rules regardless of breakpoint. Safe to
  // re-run after module swaps rebuild slides.
  class GalleryImageFit {
    // "Squarish" = natural width/height within this band of 1:1 (~15% off in
    // either direction). Widen/narrow these two numbers to taste.
    static SQUARE_MIN = 0.85;
    static SQUARE_MAX = 1.18;

    static apply(img) {
      if (!img) return;
      const decide = () => {
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        if (!w || !h) return; // not decoded / broken
        // DESKTOP ONLY. On mobile the stylesheet owns object-fit (cover, in the
        // full-bleed 4:3 frame with a 10px next-image peek), so clear any inline
        // value and let the CSS win. Only ≥1000px gets the per-image cover/
        // contain decision.
        if (!window.matchMedia('(min-width: 1000px)').matches) {
          img.style.objectFit = '';
          return;
        }
        const ratio = w / h;
        const squarish = ratio >= GalleryImageFit.SQUARE_MIN && ratio <= GalleryImageFit.SQUARE_MAX;
        img.style.objectFit = squarish ? 'cover' : 'contain';
      };
      // naturalWidth is 0 until the image decodes, so defer to `load` when it
      // isn't ready yet (lazy slides won't be on first pass).
      if (img.complete && img.naturalWidth) decide();
      else img.addEventListener('load', decide, { once: true });
    }

    static applyAll(root) {
      if (!root) return;
      root.querySelectorAll('.pg-v2__image').forEach((img) => GalleryImageFit.apply(img));
    }
  }

  class ProductGalleryV2 extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-pg-v2-track]');
      if (!this.track) return; // placeholder-only state (no media)

      this.slides = Array.from(this.querySelectorAll('[data-pg-v2-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-pg-v2-thumb]'));
      this.prevBtn = this.querySelector('[data-pg-v2-prev]');
      this.nextBtn = this.querySelector('[data-pg-v2-next]');

      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      // The scroll-snap carousel + the active-media observer run at BOTH
      // sizes now (Figma: mobile 9821:3243 swipe+dots, desktop 9145:2942
      // swipe+thumbnails). Only arrow-nav and thumb-click are desktop-only
      // affordances — gated off a live matchMedia (breakpoint 1000px, matching
      // the CSS) so a resize across it doesn't leave stale wiring behind.
      this.desktopMql = window.matchMedia('(min-width: 1000px)');

      this._onPrevClick = this.scrollByOneSlide.bind(this, -1);
      this._onNextClick = this.scrollByOneSlide.bind(this, 1);
      this._onThumbClick = this.handleThumbClick.bind(this);
      this._onVariantChange = this.handleVariantChange.bind(this);
      this._onAddonGallery = this.handleAddonGallery.bind(this);
      this.thumbsList = this.querySelector('[data-pg-v2-thumbs]');
      this.dots = Array.from(this.querySelectorAll('[data-pg-v2-dot]'));
      this.dotsTrack = this.querySelector('[data-pg-v2-dots-track]');

      if (this.prevBtn) this.prevBtn.addEventListener('click', this._onPrevClick);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this._onNextClick);
      this.thumbs.forEach((thumb) => thumb.addEventListener('click', this._onThumbClick));

      // Mouse drag-to-swipe on the carousel track. Touch already scrolls the
      // track natively, so this is limited to pointerType 'mouse' (desktop);
      // scroll-snap is turned off during the drag and restored on release so it
      // settles to the nearest slide.
      this._onDragDown = this.handleDragDown.bind(this);
      this._onDragMove = this.handleDragMove.bind(this);
      this._onDragUp = this.handleDragUp.bind(this);
      this.track.addEventListener('pointerdown', this._onDragDown);
      this.track.addEventListener('pointermove', this._onDragMove);
      this.track.addEventListener('pointerup', this._onDragUp);
      this.track.addEventListener('pointercancel', this._onDragUp);

      document.addEventListener('variant:change', this._onVariantChange);
      document.addEventListener('addon:gallery', this._onAddonGallery);

      this.setupIntersectionObserver();
      this.updateDots(0); // initial dot state; the observer corrects it once slides settle
      this._syncSingle(); // single-image galleries have no carousel chrome/drag
      GalleryImageFit.applyAll(this); // desktop: squarish -> cover, others -> contain; mobile: cleared (CSS wins)

      // Re-evaluate object-fit when crossing the 1000px breakpoint so a resize
      // doesn't leave a stale inline value (desktop sets it, mobile clears it).
      this._onFitBreakpoint = () => GalleryImageFit.applyAll(this);
      this.desktopMql.addEventListener('change', this._onFitBreakpoint);

      // Gallery-video autoplay (block setting "Autoplay gallery videos" ->
      // data-video-autoplay). Skipped for reduced-motion users.
      this.autoplayVideos = this.hasAttribute('data-video-autoplay');
      if (this.autoplayVideos && !this.prefersReducedMotion) this.setupVideoAutoplay();

      // Lead with the selected variant's media on load. Both a config reload
      // and a colour/arm section-refetch re-render this component with the
      // variant selected server-side (data-initial-media-id), so jumping here
      // makes the gallery reflect the variant without waiting for an event.
      var initialMediaId = this.dataset.initialMediaId;
      if (initialMediaId) {
        requestAnimationFrame(() => this.scrollToMediaId(initialMediaId, 'auto'));
      }

      // Mobile sticky gallery: the gallery column is pinned (CSS position:
      // sticky) while the config scrolls; release it once the ADD TO CART
      // button reaches the pinned gallery's bottom edge (component adds/removes
      // .is-released on the column). Only active on mobile (≤999px).
      this._galleryCol = this.closest('.mp-v2__gallery-col');
      if (this._galleryCol) {
        // Small-mobile only (≤600px) — tablets (e.g. iPad Air 820px) keep the
        // plain stacked scroll; must match the CSS max-width:600px block.
        this._stickyMql = window.matchMedia('(max-width: 600px)');
        this._onStickyScroll = this._onStickyScroll.bind(this);
        window.addEventListener('scroll', this._onStickyScroll, { passive: true });
        window.addEventListener('resize', this._onStickyScroll);
        this.updateMobileStickyRelease();
      }
    }

    _onStickyScroll() {
      if (this._stickyRaf) return;
      this._stickyRaf = window.requestAnimationFrame(() => {
        this._stickyRaf = null;
        this.updateMobileStickyRelease();
      });
    }

    updateMobileStickyRelease() {
      var col = this._galleryCol;
      if (!col) return;
      if (!this._stickyMql.matches) { col.style.top = ''; return; }
      var atc = document.querySelector('.bb-v2__submit');
      if (!atc) return;
      // Pinned flush to the top (top:0) — the site header hides on scroll, so
      // no header offset. The gallery stays pinned while the ADD TO CART button
      // is below its bottom edge; once the button rises past that edge, drop
      // the sticky `top` at the same rate so the button pushes the gallery up
      // and both slide off-screen together.
      var galleryH = col.offsetHeight;
      var atcTop = atc.getBoundingClientRect().top;
      if (atcTop >= galleryH) {
        col.style.top = '0px';
      } else {
        col.style.top = (atcTop - galleryH) + 'px';
      }
    }

    disconnectedCallback() {
      document.removeEventListener('variant:change', this._onVariantChange);
      if (this._onFitBreakpoint) this.desktopMql.removeEventListener('change', this._onFitBreakpoint);
      if (this._onStickyScroll) {
        window.removeEventListener('scroll', this._onStickyScroll);
        window.removeEventListener('resize', this._onStickyScroll);
      }
      document.removeEventListener('addon:gallery', this._onAddonGallery);
      if (this.track && this._onDragDown) {
        this.track.removeEventListener('pointerdown', this._onDragDown);
        this.track.removeEventListener('pointermove', this._onDragMove);
        this.track.removeEventListener('pointerup', this._onDragUp);
        this.track.removeEventListener('pointercancel', this._onDragUp);
      }
      if (this.observer) this.observer.disconnect();
      if (this.videoObserver) this.videoObserver.disconnect();
    }

    // Native <video> slides play while in the viewport and pause when scrolled
    // away. The observer root is the viewport (not the horizontal track), so a
    // slide scrolled out of the carousel is clipped -> counts as off-screen ->
    // its video pauses; only the on-screen slide's video plays.
    setupVideoAutoplay() {
      this.videos = Array.from(this.querySelectorAll('[data-pg-v2-video]'));
      if (!this.videos.length) return;
      this.videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
              video.muted = true; // required for unprompted play()
              const p = video.play();
              if (p && p.catch) p.catch(() => {});
            } else {
              video.pause();
            }
          });
        },
        { threshold: [0, 0.5] }
      );
      this.videos.forEach((video) => this.videoObserver.observe(video));
    }

    setupIntersectionObserver() {
      // Runs at both sizes: drives the desktop thumbnail highlight and the
      // mobile pagination dots.
      if (!this.slides.length) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
              this.setActiveMedia(entry.target.dataset.mediaId);
            }
          });
        },
        { root: this.track, threshold: [0.5] }
      );
      this.slides.forEach((slide) => this.observer.observe(slide));
    }

    setActiveMedia(mediaId) {
      this.thumbs.forEach((thumb) => {
        if (thumb.dataset.mediaId === mediaId) {
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
      // Mobile dots: highlight/window the dot for this slide's index.
      var index = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      this.updateDots(index);
    }

    // Windowed pagination dots (Figma 9821:3243): highlight the active dot and
    // show up to 5 total; when there are more than 5 media, translate the inner
    // track so the active dot stays ~centred and shrink the window-edge dot to
    // hint there's more beyond (Instagram style). Add-on slides (index past the
    // dot count) leave no dot active.
    updateDots(activeIndex) {
      if (!this.dots || !this.dots.length) return;
      var total = this.dots.length;
      var maxDots = 5;
      var active = activeIndex >= 0 && activeIndex < total ? activeIndex : -1;

      // Out-of-window dots are hidden (display:none), so the glass pill
      // auto-fits the visible window — no track translate needed.
      var windowStart = 0;
      if (total > maxDots) {
        var anchor = active < 0 ? 0 : active;
        windowStart = Math.min(Math.max(anchor - 2, 0), total - maxDots);
      }
      var windowEnd = Math.min(windowStart + maxDots - 1, total - 1);

      this.dots.forEach((dot, i) => {
        var inWindow = i >= windowStart && i <= windowEnd;
        var leftEdge = i === windowStart && windowStart > 0;
        var rightEdge = i === windowEnd && windowEnd < total - 1;
        dot.classList.toggle('pg-v2__dot--active', i === active);
        dot.classList.toggle('pg-v2__dot--hidden', !inWindow);
        dot.classList.toggle('pg-v2__dot--edge', inWindow && (leftEdge || rightEdge));
      });
    }

    handleDragDown(event) {
      if (event.pointerType !== 'mouse') return; // touch scrolls natively
      if (!this.slides || this.slides.length <= 1) return; // single image: no carousel to drag
      this._dragging = true;
      this._dragMoved = false;
      this._dragStartX = event.clientX;
      this._dragStartScroll = this.track.scrollLeft;
      this.track.style.scrollSnapType = 'none'; // free-scroll while dragging
      this.track.classList.add('pg-v2__track--dragging');
      try { this.track.setPointerCapture(event.pointerId); } catch (e) {}
    }

    handleDragMove(event) {
      if (!this._dragging) return;
      const dx = event.clientX - this._dragStartX;
      if (Math.abs(dx) > 3) this._dragMoved = true;
      this.track.scrollLeft = this._dragStartScroll - dx;
      event.preventDefault();
    }

    handleDragUp(event) {
      if (!this._dragging) return;
      this._dragging = false;
      this.track.style.scrollSnapType = ''; // restore mandatory snap -> nearest slide
      this.track.classList.remove('pg-v2__track--dragging');
      try { this.track.releasePointerCapture(event.pointerId); } catch (e) {}
      // Swallow the click that follows a real drag so a slide link/asset isn't
      // triggered on release.
      if (this._dragMoved) {
        this.track.addEventListener(
          'click',
          (e) => { e.stopPropagation(); e.preventDefault(); },
          { capture: true, once: true }
        );
      }
    }

    scrollByOneSlide(direction) {
      // Arrow-nav is desktop-only; on mobile the arrows are hidden (CSS)
      // and there's no horizontal track to index into.
      if (!this.desktopMql.matches || !this.track || !this.slides.length) return;
      const target = this.nearestSlideIndex() + direction;
      this.scrollToIndex(Math.max(0, Math.min(this.slides.length - 1, target)));
    }

    // Slides are narrower than the viewport (next-image peek), so navigation
    // is index-based rather than scrolling by track width.
    nearestSlideIndex() {
      const left = this.track.scrollLeft;
      let nearest = 0;
      let nearestDistance = Infinity;
      this.slides.forEach((slide, index) => {
        const distance = Math.abs(slide.offsetLeft - left);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearest = index;
        }
      });
      return nearest;
    }

    handleThumbClick(event) {
      // Thumbs are hidden (CSS) and unreachable on mobile; guard anyway
      // since module-swap thumbs are re-wired unconditionally in _rewireGallery.
      if (!this.desktopMql.matches) return;
      const thumb = event.currentTarget;
      const index = Number(thumb.dataset.index || 0);
      this.scrollToIndex(index);
    }

    // Desktop-only: indexes into the horizontal scroll-snap track.
    scrollToIndex(index) {
      if (!this.desktopMql.matches) return;
      const slide = this.slides[index];
      if (!slide || !this.track) return;
      this.track.scrollTo({
        left: slide.offsetLeft,
        behavior: this.prefersReducedMotion ? 'auto' : 'smooth',
      });
    }

    // Used by both variant-change and add-on-injection to reveal a given media
    // item. Horizontal scroll-snap carousel at all sizes now, so scroll the
    // track (not the page) to the slide.
    scrollToMediaId(mediaId, behavior) {
      if (!mediaId || !this.track) return false;
      const slide = this.slides.find((s) => s.dataset.mediaId === String(mediaId));
      if (!slide) return false;
      const scrollBehavior = behavior || (this.prefersReducedMotion ? 'auto' : 'smooth');
      this.track.scrollTo({ left: slide.offsetLeft, behavior: scrollBehavior });
      return true;
    }

    // A variant change always opens the new variant's gallery on its FIRST
    // image — never the variant's featured media, and never the slide index the
    // shopper had scrolled to on the previous variant. Reset again after layout
    // settles: the freshly swapped-in section's images are still loading, so
    // scroll-snap can re-settle the track onto a later slide.
    handleVariantChange(event) {
      const variant = event.detail && event.detail.variant;
      if (!variant) return;
      this._restoreOriginalGallery(); // a variant change reverts a module swap
      this.resetToFirstSlide();
    }

    resetToFirstSlide() {
      if (!this.track) return;
      const reset = () => {
        this.track.scrollTo({ left: 0, behavior: 'auto' });
        if (this.slides.length) this.setActiveMedia(this.slides[0].dataset.mediaId);
      };
      reset();
      requestAnimationFrame(reset);
      const firstImg = this.slides[0] && this.slides[0].querySelector('img');
      if (firstImg && !firstImg.complete) firstImg.addEventListener('load', reset, { once: true });
    }

    // Module add-ons: selecting a module REPLACES the whole gallery with that
    // module's full image set (media.gallery_variant_images, passed through
    // component-modules-addons-v2.js as addon.images). The last-selected module
    // wins; deselecting the last one restores the previous (variant/product)
    // gallery. The original gallery HTML is stashed on the first swap and put
    // back on restore.
    handleAddonGallery(event) {
      if (!this.track) return;
      const addons = (event.detail && event.detail.addons) || [];
      if (!addons.length) { this._restoreOriginalGallery(); return; }
      const active = addons[addons.length - 1];
      const images = active.images && active.images.length
        ? active.images
        : (active.image ? [active.image] : []);
      if (!images.length) { this._restoreOriginalGallery(); return; }
      this._swapToModuleImages(images, String(active.variantId));
    }

    // Swap the gallery to show only `images` (one module). Stashes the original
    // gallery markup once, then rebuilds the track / thumbs / dots from the
    // images and re-wires the observer, thumb clicks and dots.
    _swapToModuleImages(images, key) {
      if (this._activeModuleKey === key) return; // already showing this module
      if (!this._galleryStashed) {
        this._stash = {
          track: this.track.innerHTML,
          thumbs: this.thumbsList ? this.thumbsList.innerHTML : null,
          dots: this.dotsTrack ? this.dotsTrack.innerHTML : null,
        };
        this._galleryStashed = true;
      }

      const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
      this.track.innerHTML = images.map((url, i) =>
        '<div class="pg-v2__slide" data-pg-v2-slide data-media-id="mod-' + key + '-' + i + '"'
        + ' aria-roledescription="slide">'
        + '<img class="pg-v2__image" src="' + esc(url) + '" alt=""'
        + ' loading="' + (i === 0 ? 'eager' : 'lazy') + '"></div>'
      ).join('');

      if (this.thumbsList) {
        this.thumbsList.innerHTML = images.map((url, i) =>
          '<li class="pg-v2__thumb-item"><button type="button" class="pg-v2__thumb"'
          + ' data-pg-v2-thumb data-media-id="mod-' + key + '-' + i + '" data-index="' + i + '"'
          + ' aria-label="Image ' + (i + 1) + '">'
          + '<img class="pg-v2__thumb-image" src="' + esc(url) + '" alt="" loading="lazy"></button></li>'
        ).join('');
      }
      if (this.dotsTrack) {
        this.dotsTrack.innerHTML = images
          .map(() => '<span class="pg-v2__dot" data-pg-v2-dot></span>')
          .join('');
      }

      this._activeModuleKey = key;
      this._rewireGallery();
      this.track.scrollTo({ left: 0, behavior: 'auto' });
    }

    // Put the original (variant/product) gallery back after all modules are
    // deselected. No-op when nothing was swapped.
    _restoreOriginalGallery() {
      if (!this._galleryStashed) return;
      this.track.innerHTML = this._stash.track;
      if (this.thumbsList && this._stash.thumbs != null) this.thumbsList.innerHTML = this._stash.thumbs;
      if (this.dotsTrack && this._stash.dots != null) this.dotsTrack.innerHTML = this._stash.dots;
      this._galleryStashed = false;
      this._activeModuleKey = null;
      this._rewireGallery();
      if (this.dataset.initialMediaId) {
        requestAnimationFrame(() => this.scrollToMediaId(this.dataset.initialMediaId, 'auto'));
      }
    }

    // Re-query slides/thumbs/dots and re-attach the per-element wiring after the
    // track/thumbs/dots innerHTML has been rebuilt (module swap or restore).
    _rewireGallery() {
      this.slides = Array.from(this.querySelectorAll('[data-pg-v2-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-pg-v2-thumb]'));
      this.thumbs.forEach((thumb) => {
        thumb.removeEventListener('click', this._onThumbClick);
        thumb.addEventListener('click', this._onThumbClick);
      });
      this.dots = Array.from(this.querySelectorAll('[data-pg-v2-dot]'));
      if (this.observer) this.observer.disconnect();
      this.setupIntersectionObserver();
      if (this.autoplayVideos && !this.prefersReducedMotion) {
        if (this.videoObserver) this.videoObserver.disconnect();
        this.setupVideoAutoplay();
      }
      this.updateDots(0);
      this._syncSingle();
      GalleryImageFit.applyAll(this); // re-evaluate fit for the rebuilt slides
    }

    // Toggle the single-image class (no carousel): drives the CSS that hides the
    // arrows/dots/thumbnail strip, makes the lone slide full-width, and disables
    // swipe/drag/scroll on the track. Kept in sync with the live slide count so
    // module swaps that rebuild the gallery with one image are covered as well
    // as the server-rendered single-image case.
    _syncSingle() {
      this.classList.toggle('pg-v2--single', !this.slides || this.slides.length <= 1);
    }
  }

  customElements.define('product-gallery-v2', ProductGalleryV2);
}
